/**
 * Admin Contracts Page
 *
 * Organized into three tabs:
 *   - Contracts: Summary KPIs, filters, contract grid, and collapsible CRUD form
 *   - Sales: Payment link generator and tracker (designed for sales rep access)
 *   - Diagnostics: System health, webhook status, and test tools
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Package,
  RefreshCw,
  Save,
  Plus,
  Trash2,
  X,
  CreditCard,
  Activity,
  PauseCircle,
  PlayCircle,
  Loader2,
  Settings2,
} from 'lucide-react';
import { useContracts, computeContractMetrics, Contract, ContractMetrics, ContractStatus } from '@/contexts/ContractsContext';
import { useMockAgents } from '@/contexts/MockAgentsContext';
import { PaymentLinkGenerator, PaymentLinksTracker } from '@/components/admin/PaymentLinkGenerator';
import { SystemHealthDiagnostics } from '@/components/admin/SystemHealthDiagnostics';
import { ContractAmendmentDialog } from '@/components/admin/ContractAmendmentDialog';
import { ContractAmendmentHistory } from '@/components/admin/ContractAmendmentHistory';
import { ChargeExistingCustomerDialog } from '@/components/admin/ChargeExistingCustomerDialog';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';

// --- Lead Type Display Map ---

const LEAD_TYPE_LABELS: Record<string, string> = {
  life_fex: "FEX",
  life_iul: "IUL",
  life_mp: "MP",
  mortgage_purchase: "Purchase",
  mortgage_refi: "Refinance",
  mortgage_heloc: "HELOC",
  mortgage_reverse: "Reverse",
};

const ALL_LEAD_TYPES = [
  { id: "life_fex", name: "Final Expense (FEX)" },
  { id: "life_iul", name: "Indexed Universal Life (IUL)" },
  { id: "life_mp", name: "Mortgage Protection (MP)" },
  { id: "mortgage_purchase", name: "Purchase" },
  { id: "mortgage_refi", name: "Refinance" },
  { id: "mortgage_heloc", name: "HELOC" },
  { id: "mortgage_reverse", name: "Reverse Mortgage" },
];

// --- Helper Components ---

function ContractStatusBadge({ status }: { status: ContractStatus }) {
  const config: Record<ContractStatus, { label: string; className: string }> = {
    active: { label: 'Active', className: 'bg-green-500/10 text-green-600 border-green-200' },
    fulfilled: { label: 'Fulfilled', className: 'bg-blue-500/10 text-blue-600 border-blue-200' },
    expired: { label: 'Expired', className: 'bg-muted text-muted-foreground' },
    breached: { label: 'Breached', className: 'bg-destructive/10 text-destructive border-destructive/20' },
    at_risk: { label: 'At Risk', className: 'bg-orange-500/10 text-orange-600 border-orange-200' },
    cancelled: { label: 'Cancelled', className: 'bg-muted text-muted-foreground' },
    paused: { label: 'Paused', className: 'bg-red-500/10 text-red-600 border-red-200' },
  };

  const { label, className } = config[status];
  return <Badge variant="outline" className={className}>{label}</Badge>;
}

function ForecastIndicator({ metrics }: { metrics: ContractMetrics }) {
  if (metrics.isAtRisk) {
    return (
      <div className="flex items-center gap-1 text-orange-600">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-sm font-medium">At Risk</span>
      </div>
    );
  }

  if (metrics.deliveryGap > 0) {
    return (
      <div className="flex items-center gap-1 text-amber-600">
        <TrendingDown className="h-4 w-4" />
        <span className="text-sm">Behind ({Math.round(metrics.deliveryGap)} leads)</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-green-600">
      <TrendingUp className="h-4 w-4" />
      <span className="text-sm">On Track</span>
    </div>
  );
}

// --- Contract Card (simplified) ---

function ContractCard({ contract, onEdit, onPause, onResume, onModify }: {
  contract: Contract;
  onEdit: (c: Contract) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onModify?: (c: Contract) => void;
}) {
  const metrics = computeContractMetrics(contract);
  const [showDetails, setShowDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const handlePause = async () => {
    if (!onPause) return;
    setActionLoading(true);
    await onPause(contract.id);
    setActionLoading(false);
  };

  const handleResume = async () => {
    if (!onResume) return;
    setActionLoading(true);
    await onResume(contract.id);
    setActionLoading(false);
  };

  const canPause = (contract.status === 'active' || contract.status === 'at_risk') && onPause;
  const canResume = contract.status === 'paused' && onResume;
  const canModify = onModify && contract.contractType === 'monthly_target' &&
    (contract.status === 'active' || contract.status === 'at_risk' || contract.cancelAtPeriodEnd) &&
    !!contract.stripeSubscriptionId;

  return (
    <Card className={metrics.isAtRisk ? 'border-orange-300 bg-orange-50/30' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold truncate">{contract.agentName || "Unassigned"}</CardTitle>
            <div className="flex flex-wrap gap-1 mt-1">
              {contract.leadTypes && contract.leadTypes.length > 0 && (
                contract.leadTypes.map((lt) => (
                  <Badge key={lt} variant="secondary" className="text-xs">
                    {LEAD_TYPE_LABELS[lt] ?? lt}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            <ContractStatusBadge status={contract.status} />
            {(contract.rolloverLeads ?? 0) > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs">
                +{contract.rolloverLeads} rollover
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Progress Bar — primary visual */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="font-medium">
              {contract.leadsDelivered} / {contract.totalLeadsCommitted}{(contract.rolloverLeads ?? 0) > 0 ? ` +${contract.rolloverLeads}` : ''} leads
            </span>
            <span className="text-muted-foreground">
              {metrics.daysRemaining > 0 ? `${metrics.daysRemaining}d left` : 'Ended'}
            </span>
          </div>
          <Progress
            value={metrics.progressPercent}
            className={metrics.isAtRisk ? '[&>div]:bg-orange-500' : ''}
          />
        </div>

        {/* Forecast + risk inline */}
        <div className="flex items-center justify-between">
          <ForecastIndicator metrics={metrics} />
          <Badge variant="outline" className="capitalize text-xs">
            {contract.priorityTier}
          </Badge>
        </div>

        {/* Risk Warning */}
        {metrics.isAtRisk && metrics.riskReason && (
          <div className="rounded-md bg-orange-100 p-2 text-xs text-orange-800">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{metrics.riskReason}</span>
            </div>
          </div>
        )}

        {/* Expandable detail metrics */}
        {showDetails && (
          <div className="grid grid-cols-2 gap-3 text-sm border-t pt-3">
            <div>
              <span className="text-xs text-muted-foreground">Leads/Day Needed</span>
              <p className="font-medium">{metrics.leadsPerDayNeeded > 0 ? metrics.leadsPerDayNeeded.toFixed(1) : '\u2014'}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Expected by Now</span>
              <p className="font-medium">{Math.round(metrics.expectedDeliveredByNow)}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Urgency Score</span>
              <p className="font-medium">{(metrics.urgencyScore * 100).toFixed(0)}%</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Type</span>
              <p className="font-medium capitalize">{contract.contractType.replace('_', ' ')}</p>
            </div>
            {contract.notes && (
              <div className="col-span-2">
                <span className="text-xs text-muted-foreground">Notes</span>
                <p className="text-xs italic">{contract.notes}</p>
              </div>
            )}
            {contract.pricePerLead != null && (
              <div>
                <span className="text-xs text-muted-foreground">Price/Lead</span>
                <p className="font-medium">${contract.pricePerLead.toFixed(2)}</p>
              </div>
            )}
            <div className="col-span-2 pt-1">
              <ContractAmendmentHistory contractId={contract.id} />
            </div>
          </div>
        )}

        {/* Action row */}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => onEdit(contract)}>
            Edit
          </Button>
          {canModify && (
            <Button size="sm" variant="outline" onClick={() => onModify(contract)}
              className="text-blue-600 border-blue-200 hover:bg-blue-50">
              <Settings2 className="h-3.5 w-3.5 mr-1" />
              Modify
            </Button>
          )}
          {canPause && (
            <Button size="sm" variant="outline" onClick={handlePause} disabled={actionLoading}
              className="text-amber-600 border-amber-200 hover:bg-amber-50">
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5 mr-1" />}
              Pause
            </Button>
          )}
          {canResume && (
            <Button size="sm" variant="outline" onClick={handleResume} disabled={actionLoading}
              className="text-green-600 border-green-200 hover:bg-green-50">
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
              Resume
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? "Less" : "More"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Summary Cards ---

function SummaryCards({ contracts }: { contracts: Contract[] }) {
  const activeContracts = contracts.filter(c => c.status === 'active' || c.status === 'at_risk');
  const atRiskCount = contracts.filter(c => {
    if (c.status !== 'active') return c.status === 'at_risk';
    const metrics = computeContractMetrics(c);
    return metrics.isAtRisk;
  }).length;
  const fulfilledCount = contracts.filter(c => c.status === 'fulfilled').length;

  const totalCommitted = activeContracts.reduce((sum, c) => sum + c.totalLeadsCommitted, 0);
  const totalDelivered = activeContracts.reduce((sum, c) => sum + c.leadsDelivered, 0);
  const overallProgress = totalCommitted > 0 ? (totalDelivered / totalCommitted) * 100 : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Active Contracts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <span className="text-2xl font-bold">{activeContracts.length}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">At Risk</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${atRiskCount > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
            <span className={`text-2xl font-bold ${atRiskCount > 0 ? 'text-orange-600' : ''}`}>
              {atRiskCount}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Fulfilled</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <span className="text-2xl font-bold text-green-600">{fulfilledCount}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Overall Delivery</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{overallProgress.toFixed(0)}%</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {totalDelivered} / {totalCommitted} leads
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Contract Form (collapsible) ---

function ContractForm({
  contract,
  agents,
  onSave,
  onDelete,
  onClose,
}: {
  contract: Contract | null;
  agents: { id: string; name: string }[];
  onSave: (data: any, id: string | null) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const isEditing = !!contract;

  const [agentId, setAgentId] = useState(contract?.agentId ?? "");
  const [contractType, setContractType] = useState<Contract['contractType']>(contract?.contractType ?? 'monthly_target');
  const [totalLeads, setTotalLeads] = useState(contract?.totalLeadsCommitted ?? 100);
  const [cycleStart, setCycleStart] = useState(contract ? new Date(contract.cycleStart).toISOString().slice(0, 10) : "");
  const [cycleEnd, setCycleEnd] = useState(contract ? new Date(contract.cycleEnd).toISOString().slice(0, 10) : "");
  const [status, setStatus] = useState<ContractStatus>(contract?.status ?? 'active');
  const [priorityTier, setPriorityTier] = useState<'conservative' | 'balanced' | 'aggressive' | 'maximum'>(contract?.priorityTier ?? 'balanced');
  const [notes, setNotes] = useState(contract?.notes ?? "");
  const [leadTypes, setLeadTypes] = useState<string[]>(contract?.leadTypes ?? []);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    if (!agentId) { setError("Select an agent."); return; }
    if (!cycleStart || !cycleEnd) { setError("Fill in the start and end dates."); return; }
    if (new Date(cycleStart) >= new Date(cycleEnd)) { setError("Cycle start must be before cycle end."); return; }

    const agent = agents.find((a) => a.id === agentId);
    onSave({
      agentId,
      agentName: agent?.name ?? "",
      contractType,
      totalLeadsCommitted: Number(totalLeads),
      cycleStart: new Date(cycleStart).getTime(),
      cycleEnd: new Date(cycleEnd).getTime(),
      status,
      priorityTier,
      notes: notes.trim() || undefined,
      leadTypes: leadTypes.length > 0 ? leadTypes : undefined,
    }, contract?.id ?? null);
  };

  const handleDelete = () => {
    if (!contract) return;
    const confirmed = window.confirm("Are you sure you want to delete this contract?");
    if (!confirmed) return;
    onDelete(contract.id);
  };

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>{isEditing ? "Edit Contract" : "New Contract"}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Agent</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
            >
              <option value="">Select agent</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Contract Type</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={contractType}
              onChange={(e) => setContractType(e.target.value as Contract['contractType'])}
            >
              <option value="monthly_target">Monthly Target</option>
              <option value="prepaid_package">Prepaid Package</option>
              <option value="trial">Trial</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Total Leads</Label>
            <Input type="number" min={1} value={totalLeads} onChange={(e) => setTotalLeads(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Cycle Start</Label>
            <Input type="date" value={cycleStart} onChange={(e) => setCycleStart(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Cycle End</Label>
            <Input type="date" value={cycleEnd} onChange={(e) => setCycleEnd(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContractStatus)}
            >
              <option value="active">Active</option>
              <option value="at_risk">At Risk</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="expired">Expired</option>
              <option value="breached">Breached</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Delivery Velocity</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={priorityTier}
              onChange={(e) => setPriorityTier(e.target.value as any)}
            >
              <option value="conservative">Conservative</option>
              <option value="balanced">Balanced</option>
              <option value="aggressive">Aggressive</option>
              <option value="maximum">Maximum</option>
            </select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Lead Types</Label>
            <div className="flex flex-wrap gap-2 rounded-md border p-3">
              {ALL_LEAD_TYPES.map((lt) => {
                const isSelected = leadTypes.includes(lt.id);
                return (
                  <button
                    key={lt.id}
                    type="button"
                    onClick={() =>
                      setLeadTypes((prev) =>
                        isSelected ? prev.filter((id) => id !== lt.id) : [...prev, lt.id]
                      )
                    }
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-input hover:border-primary/50"
                    }`}
                  >
                    {lt.name}
                  </button>
                );
              })}
            </div>
            {leadTypes.length === 0 && (
              <p className="text-xs text-muted-foreground">No restriction — all lead types accepted</p>
            )}
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center gap-2">
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {isEditing ? "Update" : "Create"}
          </Button>
          {isEditing && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Main Page ---

export default function AdminContracts() {
  const { state, actions } = useContracts();
  const { clientAgents } = useMockAgents();
  const { role, loading: adminLoading } = useAdmin();
  const isSalesOnly = role === 'sales';
  const [filter, setFilter] = useState<'all' | 'active' | 'at_risk' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [modifyingContract, setModifyingContract] = useState<Contract | null>(null);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);

  // Update statuses on mount and periodically
  useEffect(() => {
    actions.updateContractStatuses();
    const interval = setInterval(() => {
      actions.updateContractStatuses();
    }, 60000);
    return () => clearInterval(interval);
  }, [actions]);

  const handleSave = useCallback(async (data: any, id: string | null) => {
    if (id) {
      actions.updateContract(id, data);
    } else {
      try {
        await actions.addContract(data as any);
        toast.success("Contract created successfully");
      } catch (err: any) {
        toast.error(`Failed to create contract: ${err?.message ?? "Unknown error"}`);
        return;
      }
    }
    setShowForm(false);
    setEditingContract(null);
  }, [actions]);

  const handleDelete = (id: string) => {
    actions.deleteContract(id);
    setShowForm(false);
    setEditingContract(null);
  };

  const handleEdit = (contract: Contract) => {
    setEditingContract(contract);
    setShowForm(true);
  };

  const handleNewContract = () => {
    setEditingContract(null);
    setShowForm(true);
  };

  const handleModify = (contract: Contract) => {
    setModifyingContract(contract);
  };

  const handleAmendmentSuccess = () => {
    actions.refreshContracts();
  };

  // ── Admin pause/resume handlers ──
  const handleAdminPause = useCallback(async (contractId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Authentication required. Please sign in again.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("pause-delivery", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { contractId, reason: "admin_requested" },
      });

      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch { /* ignore */ }
      }

      if (error || !data?.success) {
        const errDetail = errorBody?.detail ?? errorBody?.error ?? data?.error;
        toast.error((typeof errDetail === "string" ? errDetail : null) || "Could not pause contract.");
        return;
      }

      toast.success("Contract paused successfully");
      actions.refreshContracts();
    } catch (err) {
      console.error("[AdminContracts] Pause error:", err);
      toast.error("Could not pause contract. Please try again.");
    }
  }, [actions]);

  const handleAdminResume = useCallback(async (contractId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Authentication required. Please sign in again.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("resume-delivery", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { contractId },
      });

      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch { /* ignore */ }
      }

      if (error || !data?.success) {
        const errDetail = errorBody?.detail ?? errorBody?.error ?? data?.error;
        toast.error((typeof errDetail === "string" ? errDetail : null) || "Could not resume contract.");
        return;
      }

      toast.success("Contract resumed successfully");
      actions.refreshContracts();
    } catch (err) {
      console.error("[AdminContracts] Resume error:", err);
      toast.error("Could not resume contract. Please try again.");
    }
  }, [actions]);

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingContract(null);
  };

  // Filter contracts by status + search query
  const filteredContracts = state.contracts.filter(contract => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = (contract.agentName || '').toLowerCase().includes(q);
      const matchesEmail = (contract.agentEmail || '').toLowerCase().includes(q);
      const matchesId = contract.id.toLowerCase().includes(q);
      if (!matchesName && !matchesEmail && !matchesId) return false;
    }

    // Status filter
    switch (filter) {
      case 'active':
        return contract.status === 'active';
      case 'at_risk':
        if (contract.status === 'at_risk') return true;
        if (contract.status === 'active') {
          const metrics = computeContractMetrics(contract);
          return metrics.isAtRisk;
        }
        return false;
      case 'completed':
        return contract.status === 'fulfilled' || contract.status === 'breached' || contract.status === 'expired';
      default:
        return true;
    }
  });

  // Sort: at-risk first, then by urgency
  const sortedContracts = useMemo(() => [...filteredContracts].sort((a, b) => {
    const metricsA = computeContractMetrics(a);
    const metricsB = computeContractMetrics(b);
    if (metricsA.isAtRisk && !metricsB.isAtRisk) return -1;
    if (!metricsA.isAtRisk && metricsB.isAtRisk) return 1;
    return metricsB.urgencyScore - metricsA.urgencyScore;
  }), [filteredContracts]);

  // Wait for role to resolve before rendering — prevents flash of admin-only content for sales users
  if (adminLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </AdminLayout>
    );
  }


  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isSalesOnly ? "Sales" : "Contracts"}
            </h1>
            <p className="text-muted-foreground">
              {isSalesOnly
                ? "Generate payment links and track proposals"
                : "Manage delivery contracts, generate payment links, and monitor system health"}
            </p>
          </div>
          {!isSalesOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => actions.updateContractStatuses()}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>

        {/* Tabs — sales role defaults to Sales tab with restricted access */}
        <Tabs defaultValue={isSalesOnly ? "sales" : "contracts"}>
          <TabsList>
            {!isSalesOnly && (
              <TabsTrigger value="contracts" className="gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Contracts
              </TabsTrigger>
            )}
            <TabsTrigger value="sales" className="gap-1.5">
              <CreditCard className="h-3.5 w-3.5" />
              Sales
            </TabsTrigger>
            {!isSalesOnly && (
              <TabsTrigger value="diagnostics" className="gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                Diagnostics
              </TabsTrigger>
            )}
          </TabsList>

          {/* --- Contracts Tab (admin only) --- */}
          {!isSalesOnly && <TabsContent value="contracts" className="space-y-6">
            <SummaryCards contracts={state.contracts} />

            {/* Collapsible CRUD Form */}
            {showForm ? (
              <ContractForm
                contract={editingContract}
                agents={clientAgents}
                onSave={handleSave}
                onDelete={handleDelete}
                onClose={handleCloseForm}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleNewContract}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Contract
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setChargeDialogOpen(true)}
                  title="Debit an existing agent's saved card without sending a new payment link"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Charge saved card
                </Button>
              </div>
            )}

            {/* Search + Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Search by agent name, email, or contract ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="sm:max-w-xs"
              />
              <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All ({state.contracts.length})
              </Button>
              <Button
                variant={filter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('active')}
              >
                Active
              </Button>
              <Button
                variant={filter === 'at_risk' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('at_risk')}
                className={filter === 'at_risk' ? '' : 'text-orange-600 border-orange-200 hover:bg-orange-50'}
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                At Risk
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('completed')}
              >
                Completed
              </Button>
              </div>
            </div>

            {/* Contract Grid */}
            {sortedContracts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No contracts match the current filter</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sortedContracts.map(contract => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    onEdit={handleEdit}
                    onPause={handleAdminPause}
                    onResume={handleAdminResume}
                    onModify={handleModify}
                  />
                ))}
              </div>
            )}
          </TabsContent>}

          {/* --- Sales Tab --- */}
          <TabsContent value="sales" className="space-y-6">
            <PaymentLinkGenerator />
            <PaymentLinksTracker />
          </TabsContent>

          {/* --- Diagnostics Tab (admin only) --- */}
          {!isSalesOnly && <TabsContent value="diagnostics" className="space-y-6">
            <SystemHealthDiagnostics />
          </TabsContent>}
        </Tabs>
      </div>

      {/* Contract Amendment Dialog */}
      <ContractAmendmentDialog
        contract={modifyingContract}
        onClose={() => setModifyingContract(null)}
        onSuccess={handleAmendmentSuccess}
      />

      {/* Charge Existing Customer Dialog (admin-only — sales reps don't get this) */}
      {!isSalesOnly && (
        <ChargeExistingCustomerDialog
          open={chargeDialogOpen}
          onOpenChange={setChargeDialogOpen}
        />
      )}
    </AdminLayout>
  );
}
